import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Camera, ArrowLeft, LayoutGrid, ExternalLink, Copy, Image as ImageIcon, Plus } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useToast } from '../hooks/use-toast';

export default function GalleryIndex() {
  const { albums, currentUser } = useApp();
  const navigate = useNavigate();
  const { toast } = useToast();

  if (!currentUser) {
    navigate('/auth');
    return null;
  }

  const myAlbums = albums;

  const copyLink = (e, code) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/album/${code}`;
    navigator.clipboard.writeText(url);
    toast({ title: 'Link copiado', description: url });
  };

  return (
    <div className="grain min-h-screen">
      <header className="border-b border-[#1c1710] sticky top-0 bg-[#0b0906]/90 backdrop-blur z-30">
        <div className="max-w-6xl mx-auto px-6 md:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Link to="/painel" className="text-[#a89e87] hover:text-[#e6dcc7] inline-flex items-center gap-1.5 text-sm">
              <ArrowLeft size={16} /> Painel
            </Link>
            <div className="h-5 w-px bg-[#2a2318]" />
            <Link to="/" className="flex items-center gap-2">
              <span className="brand-mark"><Camera size={14} strokeWidth={2.2} /></span>
              <span className="font-serif text-[18px] text-[#f2ecdf]">AXSELL</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-8 py-10">
        <div className="mb-10 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-[#a89e87]">Coleção</div>
            <h1 className="font-serif text-4xl md:text-5xl text-[#f2ecdf] mt-1">Galeria de álbuns</h1>
            <p className="text-sm text-[#a89e87] mt-1">
              Todos os seus ensaios em um só lugar. Clique para ver as fotos.
            </p>
          </div>
          <div className="text-right">
            <div className="text-xs text-[#a89e87]">Álbuns</div>
            <div className="font-serif text-3xl gold-text">{myAlbums.length}</div>
          </div>
        </div>

        {myAlbums.length === 0 ? (
          <div className="card-glow rounded-2xl p-16 text-center">
            <LayoutGrid size={36} className="mx-auto text-[#3a2f20] mb-4" />
            <p className="text-[#a89e87] mb-6">Você ainda não publicou nenhum álbum.</p>
            <Link to="/painel" className="btn-gold px-5 py-2.5 rounded-md text-sm font-medium inline-flex items-center gap-2">
              <Plus size={15} /> Criar primeiro álbum
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {myAlbums.map(a => (
              <Link
                key={a.code}
                to={`/galeria/${a.code}`}
                className="group card-glow rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1"
              >
                {/* Cover mosaic */}
                <div className="relative aspect-[4/3] bg-[#12100b] overflow-hidden">
                  {(a.photoCount ?? a.photos?.length ?? 0) === 0 ? (
                    <div className="w-full h-full flex items-center justify-center text-[#3a2f20]">
                      <ImageIcon size={40} />
                    </div>
                  ) : (a.coverUrls?.length || a.photos?.length || 0) < 4 ? (
                    <img
                      src={(a.coverUrls && a.coverUrls[0]) || a.photos?.[0]?.url}
                      alt={a.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      draggable="false"
                    />
                  ) : (
                    <div className="grid grid-cols-2 grid-rows-2 gap-0.5 h-full">
                      {(a.coverUrls || (a.photos || []).slice(0, 4).map(p => p.url)).slice(0, 4).map((url, idx) => (
                        <img
                          key={idx}
                          src={url}
                          alt=""
                          className="w-full h-full object-cover"
                          draggable="false"
                        />
                      ))}
                    </div>
                  )}
                  {/* Marca d'água diagonal na capa */}
                  <div className="watermark-overlay pointer-events-none" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent pointer-events-none" />
                  <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between">
                    <div className="text-[10px] uppercase tracking-[0.14em] bg-black/60 backdrop-blur px-2 py-0.5 rounded text-[#e6dcc7]">
                      {a.photoCount ?? a.photos?.length ?? 0} foto{(a.photoCount ?? a.photos?.length ?? 0) !== 1 ? 's' : ''}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.14em] bg-[#d6af6c] text-[#1a140a] px-2 py-0.5 rounded font-semibold">
                      /album/{a.code}
                    </div>
                  </div>
                </div>

                {/* Info */}
                <div className="p-5">
                  <div className="font-serif text-xl text-[#f2ecdf] mb-1 truncate">{a.title}</div>
                  <div className="text-xs text-[#a89e87] truncate">
                    {a.client}
                    {a.date && <> • {new Date(a.date).toLocaleDateString('pt-BR')}</>}
                  </div>
                  <div className="mt-4 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-[#7d735e]">Receita</div>
                      <div className="gold-text font-medium text-sm">R$ {(a.revenue || 0).toFixed(2)}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => copyLink(e, a.code)}
                        className="btn-outline-dark p-2 rounded-md"
                        title="Copiar link"
                      >
                        <Copy size={13} />
                      </button>
                      <a
                        href={`/album/${a.code}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="btn-outline-dark p-2 rounded-md inline-flex items-center"
                        title="Ver como cliente"
                      >
                        <ExternalLink size={13} />
                      </a>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
